<template>
  <v-container fluid class="px-6 px-sm-12">
    <PageHeader
      :headerText="'Tank Details'"
      :headerIcon="'mdi-car-coolant-level'"
      :disableSiteChange="true"
    />
    <v-progress-circular
      v-if="detailsLoading"
      color="primary"
      indeterminate
      size="100"
      width="7.5"
    ></v-progress-circular>
    <div v-else>
      <v-card class="mb-6">
        <CardHeader :title="tank.tankName" icon="mdi-car-coolant-level" />
        <v-row align="center" class="mx-4">
          <v-col cols="12" lg="6" xl="6">
            <TankStatusCard
              class="mx-auto mb-8"
              :tank="tank"
              :progressCircleSize="progressCircleSize"
              :progressCircleWidth="progressCircleWidth"
              :max-width="$vuetify.breakpoint.mobile ? '' : '600px'"
              :onUpdateTankStatusClicked="openUpdateStatusDialog"
            />
          </v-col>
          <v-col cols="12" lg="6" xl="6">
            <div>
              <h3 class="headline primary--text font-weight-medium text-start">
                Tank Details
              </h3>
              <v-divider></v-divider>
              <v-row>
                <v-col
                  cols="12"
                  sm="6"
                  md="6"
                  lg="6"
                  xl="6"
                  v-for="detail in detailsArray"
                  :key="detail.key"
                >
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon color="primary">{{ detail.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ detail.value }}</v-list-item-title>
                      <v-list-item-subtitle>{{
                        detail.key
                      }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
                <v-col cols="12">
                  <v-list>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon color="primary">mdi-message</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        {{ tank.comments ? tank.comments : "..." }}
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>
            </div>

            <div>
              <h3 class="headline primary--text font-weight-medium text-start">
                Current Status
              </h3>
              <v-divider></v-divider>
              <v-row v-if="currentTankStatus">
                <v-col
                  cols="12"
                  sm="6"
                  md="6"
                  lg="6"
                  xl="6"
                  v-for="detail in currentStatusDetails"
                  :key="detail.key"
                >
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon color="primary">{{ detail.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ detail.value }}</v-list-item-title>
                      <v-list-item-subtitle>{{
                        detail.key
                      }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
                <v-col cols="12">
                  <v-list>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon color="primary">mdi-message</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="text-start">
                        {{
                          currentTankStatus.remarks
                            ? currentTankStatus.remarks
                            : "..."
                        }}
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>
              <v-row v-else>
                <v-col cols="12">
                  <v-alert type="info" outlined color="warning"
                    >No Tank Status Recorded</v-alert
                  >
                </v-col>
              </v-row>
            </div>
          </v-col>
        </v-row>
      </v-card>
      <v-card>
        <CardHeader title="Tank History" icon="mdi-history" />
        <v-data-table
          :headers="visibleTankHistoryHeaders"
          :items="tankStatusHistory"
          :items-per-page="10"
          :item-key="isClientConnectedToServer ? 'id' : 'idc'"
          sort-by="statusDateTimeLt"
          :sort-desc="true"
          :mobile-breakpoint="dataTableMobileBreakpoint"
          class="elevation-1"
          no-data-text="No Tank Status Found"
        >
          <template v-slot:item.syncState="{ item }">
            <SyncStateIcon
              v-if="isAnyUnsyncedStatuses"
              :isItemUnsynced="item.idc ? true : false"
            ></SyncStateIcon>
          </template>

          <template v-slot:item.fillLevel="{ item }">
            <TablePercentageBar
              :value="item.fillPercent"
              :color="getProgressColourByFillPercent(item.fillPercent)"
            />
          </template>

          <template v-slot:item.actions="{ item }">
            <v-icon
              v-if="item.canDelete"
              :size="iconSize"
              @click.stop="openDeleteDialog(item)"
              :disabled="
                (!isClientConnectedToServer && !item.idc) || !isTankAdmin
              "
              >mdi-delete</v-icon>
          </template>
        </v-data-table>
      </v-card>
      <!-- Update Status Dialog -->
      <v-dialog v-model="updateTankStatusDialog" max-width="950px">
        <v-card>
          <TankStatusForm
            :status="tankStatus"
            :onSubmitClick="submitTankStatus"
            :onCloseClick="closeUpdateStatusDialog"
          />
        </v-card>
      </v-dialog>
      <ConfirmDialog
        v-model="showDeleteDialog"
        title="Delete Tank Status"
        description="Are you sure you want to delete this Tank Status?"
        :onConfirmClick="deleteTankStatus"
        :onCloseClick="closeDeleteDialog"
      />
    </div>
  </v-container>
</template>

<script>
  import { mapGetters, mapState } from 'vuex';
  import TankStatusMixin from '../mixins/TankStatusMixin';
  import ResponsiveSizeHelperMixin from '../mixins/ResponsiveSizeHelperMixin';
  import PageHeader from '../components/ui/PageHeader.vue';
  import CardHeader from '../components/ui/CardHeader.vue';
  import TankStatusCard from '../components/tanks/TankStatusCard.vue';
  import TankStatusForm from '../components/tanks/TankStatusForm.vue';
  import ConfirmDialog from '../components/ui/ConfirmDialog.vue';
  import SyncStateIcon from '../components/ui/SyncStateIcon.vue';
  import TablePercentageBar from '../components/ui/TablePercentageBar.vue';

  export default {
    name: 'TankDetails',
    mixins: [TankStatusMixin, ResponsiveSizeHelperMixin],
    components: {
      PageHeader,
      CardHeader,
      TankStatusCard,
      TankStatusForm,
      ConfirmDialog,
      SyncStateIcon,
      TablePercentageBar,
    },
    data() {
      return {
        detailsLoading: false,
        showDeleteDialog: false,
        tankHistoryHeaders: [
          { text: 'Status Date (Lt)', value: 'statusDateTimeLt' },
          { text: 'Recording', value: 'recordingType' },
          { text: 'Type', value: 'transactionType' },
          { text: 'Bulk', value: 'bulk' },
          { text: 'Exchanged', value: 'exchanged' },
          { text: 'Fill Level', value: 'quantityOnBoard' },
          { text: 'Fill Level (%)', value: 'fillLevel' },
          { text: 'SG', value: 'specificGravity' },
          { text: 'Actions', value: 'actions' },
        ],
        syncStateHeader: { text: '', value: 'syncState' },
      };
    },
    computed: {
      ...mapState(['tanksStore']),
      ...mapGetters({
        isClientConnectedToServer: 'userStore/isClientConnectedToServer',
        getActiveSiteId: 'userStore/getActiveSiteId',
        isTankAdmin: 'userStore/isTankAdmin',
      }),
      isTankStateLoaded() {
        return !!this.tanksStore.tanksWithCurrentStatus
          && this.tanksStore.tanksWithCurrentStatus.length > 0;
      },
      tankDetails() {
        const details = this.tanksStore ? this.tanksStore.tankDetails : null;
        return details;
      },
      tank() {
        return this.tankDetails ? this.tankDetails.tank : null;
      },
      tankStatusHistory() {
        if (!this.tankDetails)
          return null;

        const history = this.tankDetails.tankStatusHistory.map(h => {

          const transaction = this.tankDetails.relatedBulkTransactions
            .find(bt => bt.tankStatusId === h.id);

          // Recording + Transaction type string
          const recordingType = h.recordingType;
          const transactionType = transaction ? transaction.transactionType : null;

          // Change
          const exchanged = transaction ? transaction.quantity : '';
          const direction = transactionType ?
            transactionType === "Loaded" || transactionType === "Generated" ? "+" : "-" : "";
          const exchangedString = `${direction}${exchanged}`;

          return {
            ...h,
            recordingType,
            transactionType,
            exchanged: exchangedString,
            canDelete: transaction ? false : true,
            quantityOnBoard: h.quantityOnBoard.toFixed(2)
          };
        });

        console.log("History", history);

        return history;
      },
      visibleTankHistoryHeaders() {
        if (this.isAnyUnsyncedStatuses) {
          const tankHistoryHeadersForOffline = [...this.tankHistoryHeaders];
          tankHistoryHeadersForOffline.unshift(this.syncStateHeader);

          return tankHistoryHeadersForOffline;
        }
        return this.tankHistoryHeaders;
      },
      currentTankStatus() {
        return this.tank ? this.tank.currentTankStatus : null;
      },
      detailsArray() {
        return [
          { key: 'Vessel', value: this.tank.vesselName, icon: 'mdi-ferry' },
          { key: 'Bulk', value: this.tank.bulkName, icon: 'mdi-water' },
          { key: 'Tank Type', value: this.tank.tankBulkType, icon: 'mdi-car-coolant-level' },
          { key: 'Maximum Capacity', value: `${this.tank.maxCapacity} ${this.tank.qtyUom}`, icon: 'mdi-speedometer' },
        ];
      },
      currentStatusDetails() {
        return [
          { key: 'Last Time Recorded', value: this.$options.filters.removeTAndSecondsAndFormatDate(this.currentTankStatus.statusDateTimeLt), icon: 'mdi-clock' },
          { key: 'Bulk', value: this.currentTankStatus.bulk, icon: 'mdi-water' },
          { key: 'Quantity On Board', value: `${this.currentTankStatus.quantityOnBoard.toFixed(2)} ${this.tank.qtyUom}`, icon: 'mdi-speedometer-medium' },
          { key: 'Fill Percent', value: `${this.currentTankStatus.fillPercent}%`, icon: 'mdi-thermometer' },
          { key: 'Specific Gravity', value: this.currentTankStatus.specificGravity, icon: 'mdi-calculator-variant' },
          { key: 'Well/Put No.', value: this.currentTankStatus.wellNumber ?? '...', icon: 'mdi-pound-box' },
          { key: 'Backload', value: this.currentTankStatus.isBackload ? 'Yes' : 'No', icon: 'mdi-download-multiple' },
          { key: 'Offshore Location.', value: this.currentTankStatus.offshoreLocation ?? '...', icon: 'mdi-map-marker' },
          { key: 'Load Date', value: this.currentTankStatus.loadDate ?? '...', icon: 'mdi-sort-calendar-descending' },
          { key: 'Discharge Date', value: this.currentTankStatus.dischargeDate ?? '...', icon: 'mdi-sort-calendar-ascending' },
        ];
      },
      progressCircleSize() {
        switch (this.$vuetify.breakpoint.name) {
          case 'xs':
            return 225;
          default:
            return 450;
        }
      },
      progressCircleWidth() {
        switch (this.$vuetify.breakpoint.name) {
          case 'xs':
            return 35;
          default:
            return 50;
        }
      },
    },
    watch: {
      isClientConnectedToServer(newVal) {
        if (newVal) this.initStateAndLoadDetails();
      },
    },
    methods: {
      initStateAndLoadDetails() {
        if (!this.isTankStateLoaded) {
          this.detailsLoading = true;
          this.$store.dispatch('tanksStore/loadTankStatuses', this.getActiveSiteId)
            .then(() => this.loadTankDetails());
        }
        this.loadTankDetails();
      },
      loadTankDetails() {
        this.detailsLoading = true;
        this.$store.dispatch('tanksStore/loadTankDetails', this.$route.params.id)
          .finally(() => { this.detailsLoading = false; });
      },
      openDeleteDialog(item) {
        this.selectedStatus = { ...item };
        this.showDeleteDialog = true;
      },
      closeDeleteDialog() {
        this.selectedStatus = {};
        this.showDeleteDialog = false;
      },
      deleteTankStatus() {
        const { id, idc } = this.selectedStatus;
        this.$store.dispatch('tanksStore/deleteTankStatus', { id, idc })
          .then(() => {
            this.onSuccess('Tank Status Deleted');
            this.closeDeleteDialog();
          })
          .then(() => {
            this.loadTankDetails();
          })
          .catch((err) => this.onError('Failed to Delete Tank Status', err));
      },
    },
    created() {
      this.initStateAndLoadDetails();
    },
    beforeDestroy() {
      this.$store.dispatch('tanksStore/clearTankDetails');
    },
  };
</script>
